// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/LanguageNode.js (464 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 *
 * Preserves verbatim: DTMF_KEYS ordering ["0"..."9","#","*"] (differs from the
 * shared constant which ends "*","#" — this local order is what the wire JSON
 * expects), keyLangMap → selectedLanguages derivation on save, per-key
 * language-exclusion in getAvailableForKey, menu/noInputFileId/wrongInputFileId
 * action shape, `selectedHandles: {}` reset on save.
 */
import React, { useContext, useState } from 'react';
import { Button, Form } from './_rbShim';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Globe, Settings, Music } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { triggerHandleConnect } from '../Helpers/utils';
import { NodesContext } from '../provider/IvrContext';
import { getApiCall } from '@/service/httpCall';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { toast } from 'sonner';

const COLOR = '#0d9488';
const DTMF_KEYS = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '#',
  '*',
];

const LanguageNode = ({ data, id }: any) => {
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;
  const { getNodes } = useReactFlow();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [audioPlay, setAudioPlay] = useState<any>(null);
  const [audioType, setAudioType] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  const [promptList, setPromptList] = useState<any[]>([]);
  const [answerLangs, setAnswerLangs] = useState<any[]>([]); // [{ code, name }]

  const {
    action = {},
    timeout = '',
    rePrompt = '',
    keyLangMap = {},
    selectedLanguages = [],
  } = data;

  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftTimeout, setDraftTimeout] = useState(timeout);
  const [draftRePrompt, setDraftRePrompt] = useState(rePrompt);
  const [draftKeyLangMap, setDraftKeyLangMap] = useState<any>(keyLangMap);
  const [draftLangSelects, setDraftLangSelects] = useState<any>({
    menu: action?.menu?.language || '',
    noInputFileId: action?.noInputFileId?.language || '',
    wrongInputFileId: action?.wrongInputFileId?.language || '',
  });

  const openDrawer = async () => {
    // Read AnswerNode languages
    const answerNode = getNodes().find((n: any) => n.type === 'answerNode');
    const codes = Array.isArray(answerNode?.data?.languages)
      ? answerNode.data.languages
      : [];
    const names = Array.isArray(answerNode?.data?.languageNames)
      ? answerNode.data.languageNames
      : [];
    const langs = codes.map((code: any, i: number) => ({
      code,
      name: names[i] || code,
    }));
    setAnswerLangs(langs);

    // Fetch prompts
    try {
      const resp: any = await getApiCall(
        `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
      );
      if (Array.isArray(resp)) {
        setPromptList(
          resp.filter(
            (p: any) =>
              p.prompt_category === 'general' &&
              Array.isArray(p.prompt_url) &&
              p.prompt_url.some((e: any) => e.status === 1),
          ),
        );
      }
    } catch (err) {
      console.error(err);
    }

    setDraftAction(action);
    setDraftTimeout(timeout);
    setDraftRePrompt(rePrompt);
    setDraftKeyLangMap({ ...keyLangMap });
    setDraftLangSelects({
      menu: action?.menu?.language || '',
      noInputFileId: action?.noInputFileId?.language || '',
      wrongInputFileId: action?.wrongInputFileId?.language || '',
    });
    setErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setErrors({});
    setDrawerOpen(false);
  };

  // Audio filter: use AnswerNode's language codes (or no restriction)
  const answerLangCodes = answerLangs.map((l: any) => l.code);

  const filteredPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const updateActionAudio = (key: string, value: string) => {
    if (value === '') {
      setDraftAction((prev: any) => ({ ...prev, [key]: null }));
      setDraftLangSelects((prev: any) => ({ ...prev, [key]: '' }));
      return;
    }
    const sel = promptList.find((i: any) => i.id.toString() === value);
    if (!sel) return;
    const approvedLangs = Array.isArray(sel.prompt_url)
      ? sel.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];
    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction((prev: any) => ({
      ...prev,
      [key]: {
        id: sel.id,
        audio: autoAudio,
        duration: sel?.prompt_duration ?? 0,
        name: sel.prompt_name,
        language: autoLang,
      },
    }));
    setDraftLangSelects((prev: any) => ({ ...prev, [key]: autoLang }));
  };

  const updateActionLanguage = (key: string, lang: string) => {
    setDraftLangSelects((prev: any) => ({ ...prev, [key]: lang }));
    const promptId = draftAction?.[key]?.id;
    const sel = promptList.find(
      (i: any) => i.id?.toString() === promptId?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((e: any) => e.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      [key]: { ...prev?.[key], audio: entry?.url || '', language: lang },
    }));
  };

  // Key → Language helpers
  const toggleKey = (key: string) => {
    setDraftKeyLangMap((prev: any) => {
      const updated = { ...prev };
      if (key in updated) {
        delete updated[key];
      } else {
        updated[key] = '';
      }
      return updated;
    });
  };

  const setKeyLang = (key: string, langCode: string) => {
    setDraftKeyLangMap((prev: any) => ({ ...prev, [key]: langCode }));
  };

  const getAvailableForKey = (key: string) => {
    const usedByOthers = Object.entries(draftKeyLangMap)
      .filter(([k, v]) => k !== key && v)
      .map(([, v]) => v);
    return answerLangs.filter((l: any) => !usedByOthers.includes(l.code));
  };

  const validateForm = () => {
    const errs: any = {};
    if (!draftAction?.menu?.id) errs.audio = 'Menu audio is required';
    if (draftAction?.menu?.id && !draftLangSelects.menu)
      errs.menuLanguage = 'Please select a language for menu audio';
    if (draftAction?.noInputFileId?.id && !draftLangSelects.noInputFileId)
      errs.noInputLanguage = 'Please select a language';
    if (
      draftAction?.wrongInputFileId?.id &&
      !draftLangSelects.wrongInputFileId
    )
      errs.wrongInputLanguage = 'Please select a language';
    if (draftTimeout === '' || draftTimeout === null)
      errs.timeout = 'Wait time is required';

    const entries = Object.entries(draftKeyLangMap);
    if (entries.length === 0) {
      errs.keys = 'Select at least one key and assign a language';
    } else {
      const unassigned = entries
        .filter(([, v]) => !v)
        .map(([k]) => k);
      if (unassigned.length > 0)
        errs.keys = `Select a language for key(s): ${unassigned.join(', ')}`;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const newSelectedLanguages = Object.values(draftKeyLangMap).filter(Boolean);
    updateNodeData(id, {
      ...data,
      action: draftAction,
      timeout: draftTimeout,
      rePrompt: draftRePrompt,
      keyLangMap: draftKeyLangMap,
      selectedLanguages: newSelectedLanguages,
      selectedHandles: {},
    });
    toast.success('Language node saved.');
    setDrawerOpen(false);
  };

  const menuAudioName = action?.menu?.name || '';
  const displayText = Object.entries(keyLangMap)
    .filter(([, v]) => v)
    .map(([k, code]: any) => `${k}→${code.toUpperCase()}`)
    .join(', ');

  const renderLangSelect = (key: string, list: any[]) => {
    const sel = list.find(
      (a: any) => a.id?.toString() === draftAction?.[key]?.id?.toString(),
    );
    const langs = Array.isArray(sel?.prompt_url)
      ? sel.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];
    if (!sel || langs.length === 0) return null;
    const errKey =
      key === 'menu'
        ? 'menuLanguage'
        : key === 'noInputFileId'
        ? 'noInputLanguage'
        : 'wrongInputLanguage';
    return (
      <>
        <Form.Label
          style={{ marginTop: 8, marginBottom: 4, fontWeight: 500 }}
        >
          Select Audio Language{' '}
          {key === 'menu' && <span style={{ color: 'red' }}>*</span>}
        </Form.Label>
        <Form.Select
          value={draftLangSelects[key]}
          onChange={(e) => updateActionLanguage(key, e.target.value)}
        >
          <option value="">Select Language</option>
          {langs.map((e: any) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Select>
        {errors[errKey] && (
          <Form.Text className="text-danger">{errors[errKey]}</Form.Text>
        )}
      </>
    );
  };

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': COLOR } as any}>
      <span
        className="ivrn-icon"
        style={{ background: COLOR, color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <Globe size={22} strokeWidth={2} />
      </span>

      <Handle
        type="target"
        position={Position.Left}
        style={{ background: COLOR, top: '65%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: COLOR }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Language</span>
        {selectedLanguages.length > 0 && (
          <span
            className="ivrn-badge"
            style={{ background: '#ccfbf1', color: '#0f766e' }}
          >
            {selectedLanguages.length} lang
            {selectedLanguages.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="ivrn-body">
        {menuAudioName ? (
          <span className="ivrn-val">
            <Music size={10} style={{ marginRight: 4, flexShrink: 0 }} />
            {menuAudioName}
          </span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to configure</span>
        )}
        {displayText && (
          <span className="ivrn-val">
            <Globe
              size={10}
              style={{ color: COLOR, marginRight: 4, flexShrink: 0 }}
            />
            {displayText}
          </span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className={`ivrn-btn ivrn-btn--link${
            selectedLanguages.length > 0 ? ' active' : ''
          }`}
          title="Configure"
          onClick={openDrawer}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--delete"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          ×
        </button>
      </div>

      <IvrDrawer
        show={drawerOpen}
        onHide={closeDrawer}
        title="Language Node"
        subtitle="Play a menu, map keys to languages, and route the call."
        footer={
          <>
            <Button variant="secondary" onClick={closeDrawer}>
              Discard
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        {/* Basic Settings */}
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Basic Settings</div>
          <Form.Group className="mb-3">
            <Form.Label>
              Menu Audio <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftAction?.menu?.id || ''}
              onChange={(e) => updateActionAudio('menu', e.target.value)}
            >
              <option value="">Select audio file</option>
              {filteredPromptList.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.prompt_name}
                </option>
              ))}
            </Form.Control>
            {renderListenBtn(
              draftAction?.menu?.audio ||
                draftAction?.menu?.languages?.[0]?.url ||
                '',
              draftAction?.menu?.name,
            )}
            {errors.audio && (
              <Form.Text className="text-danger">{errors.audio}</Form.Text>
            )}
            {renderLangSelect('menu', promptList)}
          </Form.Group>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <Form.Group>
              <Form.Label>
                Wait time (s) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={draftTimeout}
                onChange={(e) => setDraftTimeout(e.target.value)}
              >
                <option value="">Select</option>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
              {errors.timeout && (
                <Form.Text className="text-danger">{errors.timeout}</Form.Text>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Re-prompt count</Form.Label>
              <Form.Select
                value={draftRePrompt}
                onChange={(e) => setDraftRePrompt(e.target.value)}
              >
                <option value="">Select</option>
                {[0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>

        {/* Fallback Audio */}
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Fallback Audio</div>
          <Form.Group className="mb-3">
            <Form.Label>
              No-input audio{' '}
              <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                (optional)
              </span>
            </Form.Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Select
                style={{ flex: 1 }}
                value={draftAction?.noInputFileId?.id || ''}
                onChange={(e) =>
                  updateActionAudio('noInputFileId', e.target.value)
                }
              >
                <option value="">Pick (or leave blank)</option>
                {filteredPromptList.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.prompt_name}
                  </option>
                ))}
              </Form.Select>
              {draftAction?.noInputFileId?.audio && (
                <button
                  type="button"
                  className="ivr-drawer-play-btn"
                  onClick={() => {
                    setAudioPlay(draftAction.noInputFileId.audio);
                    setAudioType('No Input Audio');
                    setPlayModalOpen(true);
                  }}
                >
                  ▶
                </button>
              )}
            </div>
            {renderLangSelect('noInputFileId', promptList)}
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Wrong-input audio{' '}
              <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                (optional)
              </span>
            </Form.Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Select
                style={{ flex: 1 }}
                value={draftAction?.wrongInputFileId?.id || ''}
                onChange={(e) =>
                  updateActionAudio('wrongInputFileId', e.target.value)
                }
              >
                <option value="">Pick (or leave blank)</option>
                {filteredPromptList.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.prompt_name}
                  </option>
                ))}
              </Form.Select>
              {draftAction?.wrongInputFileId?.audio && (
                <button
                  type="button"
                  className="ivr-drawer-play-btn"
                  onClick={() => {
                    setAudioPlay(draftAction.wrongInputFileId.audio);
                    setAudioType('Wrong Input Audio');
                    setPlayModalOpen(true);
                  }}
                >
                  ▶
                </button>
              )}
            </div>
            {renderLangSelect('wrongInputFileId', promptList)}
          </Form.Group>
        </div>

        {/* Key → Language Mapping */}
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Key → Language</div>
          {answerLangs.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>
              No languages configured in Answer node. Open Answer node settings
              and add languages first.
            </p>
          ) : (
            <>
              {errors.keys && (
                <div
                  style={{
                    fontSize: 12,
                    color: '#ef4444',
                    marginBottom: 10,
                  }}
                >
                  {errors.keys}
                </div>
              )}

              {/* Compact key grid — tap a key to toggle it on/off */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: 8,
                }}
              >
                {DTMF_KEYS.map((key) => {
                  const active = key in draftKeyLangMap;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleKey(key)}
                      style={{
                        height: 40,
                        border: active
                          ? '2px solid #6366f1'
                          : '1.5px solid #dde1e9',
                        borderRadius: 10,
                        background: active ? '#eef2ff' : '#fafbfc',
                        color: active ? '#4338ca' : '#6b7280',
                        fontWeight: active ? 700 : 500,
                        fontSize: 14,
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>

              {/* Language dropdown — one row per active key only */}
              {Object.keys(draftKeyLangMap).length > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {DTMF_KEYS.filter((key) => key in draftKeyLangMap).map(
                    (key) => {
                      const available = getAvailableForKey(key);
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                        >
                          <span
                            style={{
                              minWidth: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#6366f1',
                              color: '#fff',
                              borderRadius: 8,
                              fontWeight: 700,
                              fontSize: 13,
                              flexShrink: 0,
                              fontFamily: 'monospace',
                            }}
                          >
                            {key}
                          </span>
                          <Form.Select
                            size="sm"
                            style={{ flex: 1 }}
                            value={draftKeyLangMap[key] || ''}
                            onChange={(e) => setKeyLang(key, e.target.value)}
                          >
                            <option value="">Select language</option>
                            {available.map((l: any) => (
                              <option key={l.code} value={l.code}>
                                {l.name}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      );
                    },
                  )}
                </div>
              )}

              <div
                style={{
                  fontSize: 11.5,
                  color: '#94a3b8',
                  marginTop: 12,
                }}
              >
                Tap a key to assign it a language. Once assigned, it won't
                appear in other key dropdowns.
              </div>
            </>
          )}
        </div>
      </IvrDrawer>

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={audioPlay}
        title={audioType}
      />

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default LanguageNode;
